import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Share2, Minus, Plus, ShoppingBag, ArrowLeft, ArrowRight, Heart, Instagram } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProductSlider } from "@/components/ProductSlider";
import { FrequentlyBoughtTogether } from "@/components/FrequentlyBoughtTogether";
import { DeliveryCheck } from "@/components/DeliveryCheck";
import { SaleCountdown } from "@/components/SaleCountdown";
import { reelId, embedUrl } from "@/components/ReelsModal";
import { RECENT_KEY } from "@/components/RecentlyViewed";
import { Reviews } from "@/components/Reviews";

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add, setIsOpen } = useCart();
  const { toggle, has } = useWishlist();
  const [product, setProduct] = useState(null);
  const [brands, setBrands] = useState([]);
  const [siteReels, setSiteReels] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [alsoViewed, setAlsoViewed] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState("");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null);
    setActiveImg(0);
    setQty(1);
    setVariant("");
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSiteReels((r.data.instagram_reels || []).map((x) => (typeof x === "string" ? { url: x, product_id: "" } : x)))).catch(() => {});
    try {
      const ids = JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
      localStorage.setItem(RECENT_KEY, JSON.stringify([id, ...ids.filter((x) => x !== id)].slice(0, 12)));
    } catch {}
  }, [id]);

  useEffect(() => {
    if (!product) return;
    api.get("/products").then((r) => {
      const all = r.data.filter((p) => p.id !== product.id);
      setSuggested(all.filter((p) => p.brand_slug === product.brand_slug).slice(0, 12));
      const sameCat = all.filter((p) => p.brand_slug !== product.brand_slug && p.category === product.category);
      const fill = all.filter((p) => p.brand_slug !== product.brand_slug && p.category !== product.category);
      setAlsoViewed([...sameCat, ...fill].slice(0, 12));
    }).catch(() => {});
  }, [product]);

  // Track reel views on the product page (once, when half-visible)
  useEffect(() => {
    if (!product) return;
    const b = brands.find((x) => x.slug === product.brand_slug);
    const rid = reelId(product.instagram_reel || b?.instagram_reel || siteReels.find((r) => r.product_id === product.id)?.url);
    if (!rid) return;
    const el = document.querySelector('[data-testid="product-reel"]');
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) {
          api.post("/analytics/reel-view", { reel_id: rid, surface: "pdp" }).catch(() => {});
          obs.disconnect();
        }
      });
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [product, brands, siteReels]);

  if (notFound) {
    return (
      <main>
        <Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center gap-8" data-testid="product-not-found">
          <p className="font-serif italic text-4xl text-zinc-500">This piece has left the collection.</p>
          <Link to="/shop" className="text-xs uppercase tracking-[0.3em] text-gold border-b border-gold/40 pb-1">
            Back to the Collection
          </Link>
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-zinc-400 text-xs uppercase tracking-[0.4em]">Unveiling…</p>
        </div>
      </main>
    );
  }

  const brand = brands.find((b) => b.slug === product.brand_slug);
  const reelUrl = product.instagram_reel || brand?.instagram_reel || siteReels.find((r) => r.product_id === product.id)?.url || "";
  const gallery = [product.image, ...(product.images || [])].filter(Boolean);
  const selectedVariant = (product.variants || []).find((v) => v.title === variant);
  const displayPrice = selectedVariant?.price ?? product.price;
  const comparePrice = (product.compare_at && product.compare_at > displayPrice) ? product.compare_at
    : (product.mrp && product.mrp > displayPrice ? product.mrp : 0);

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied — share away");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  return (
    <main data-testid="product-page">
      <Navbar />
      <section className="pt-32 md:pt-40 pb-24 relative overflow-hidden">
        <div className="spotlight absolute inset-0" aria-hidden="true" />
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 relative z-10">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-14">
            <button onClick={() => navigate("/shop")} className="flex items-center gap-2 hover:text-gold transition-colors duration-300" data-testid="product-back-link">
              <ArrowLeft size={12} /> The Collection
            </button>
            <span>/</span>
            <span className="text-gold">{brand?.name || product.brand_slug}</span>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="md:col-span-6">
              <div className="gold-frame">
                <div className="bg-[#faf7f7] border border-zinc-100 flex items-center justify-center p-10 overflow-hidden">
                  <motion.img
                    key={activeImg}
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    src={gallery[activeImg]}
                    alt={product.name}
                    className="max-h-[60vh] object-contain"
                    data-testid="product-main-image"
                  />
                </div>
              </div>
              {gallery.length > 1 && (
                <div className="flex gap-4 mt-6" data-testid="product-gallery">
                  {gallery.map((src, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      data-testid={`product-thumb-${i}`}
                      className={`w-20 h-24 bg-[#faf7f7] border flex items-center justify-center p-2 transition-colors duration-300 ${i === activeImg ? "border-gold" : "border-zinc-200 hover:border-zinc-300"}`}
                    >
                      <img src={src} alt="" className="max-w-full max-h-full object-contain" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }} className="md:col-span-6 flex flex-col">
              <p className="text-[10px] uppercase tracking-[0.4em] text-gold">{brand?.name || product.brand_slug}</p>
              <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-6xl text-zinc-900 tracking-tight leading-[1.08] mt-4" data-testid="product-name">
                {product.name}
              </h1>
              <div className="flex items-center gap-5 mt-6">
                {product.rating && (
                  <span className="flex items-center gap-1.5 text-sm text-zinc-500">
                    <Star size={13} className="fill-gold text-gold" /> {product.rating} / 5
                  </span>
                )}
                {product.category && (
                  <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 border border-zinc-200 px-3 py-1.5">{product.category}</span>
                )}
                <span className={`text-[10px] uppercase tracking-[0.25em] ${product.in_stock ? "text-emerald-400" : "text-red-400"}`} data-testid="product-stock">
                  {product.in_stock ? "In Stock" : "Out of Stock"}
                </span>
              </div>
              <div className="flex items-baseline gap-4 mt-8 pb-8 border-b border-zinc-200">
                <span className="font-serif text-5xl text-gold" data-testid="product-price">{inr(displayPrice)}</span>
                {comparePrice > displayPrice && (
                  <>
                    <span className="text-zinc-400 text-lg line-through">{inr(comparePrice)}</span>
                    <span className="text-[10px] uppercase tracking-[0.2em] bg-gold/15 text-gold px-2.5 py-1">
                      Save {Math.round((1 - displayPrice / comparePrice) * 100)}%
                    </span>
                  </>
                )}
                {product.on_sale && (
                  <span className="text-[10px] uppercase tracking-[0.2em] bg-red-500 text-white px-2.5 py-1" data-testid="product-sale-tag">Sale</span>
                )}
              </div>
              {product.on_sale && product.sale_end && <SaleCountdown endsAt={product.sale_end} />}
              {(product.variants || []).length > 0 && (
                <div className="mt-8" data-testid="variant-picker">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-3">Select size / option</p>
                  <div className="flex flex-wrap gap-3">
                    {product.variants.map((v) => (
                      <button
                        key={v.title}
                        onClick={() => setVariant(variant === v.title ? "" : v.title)}
                        data-testid={`variant-${v.title.replace(/\s+/g, "-").toLowerCase()}`}
                        className={`px-5 py-2.5 border text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${variant === v.title ? "bg-gold text-white border-gold" : "border-zinc-200 text-zinc-600 hover:border-gold/50 hover:text-gold"}`}
                      >
                        {v.title}{v.price ? ` · ${inr(v.price)}` : ""}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-zinc-500 leading-relaxed mt-8 flex-1" data-testid="product-description">
                {product.description || "A curated formula from the house — details arriving soon."}
              </p>
              <div className="flex flex-wrap items-center gap-5 mt-10">
                <div className="flex items-center border border-zinc-200">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="product-qty-minus" className="p-4 text-zinc-500 hover:text-gold transition-colors duration-200" aria-label="Decrease quantity">
                    <Minus size={14} />
                  </button>
                  <span className="w-10 text-center text-zinc-900" data-testid="product-qty">{qty}</span>
                  <button onClick={() => setQty(Math.min(20, qty + 1))} data-testid="product-qty-plus" className="p-4 text-zinc-500 hover:text-gold transition-colors duration-200" aria-label="Increase quantity">
                    <Plus size={14} />
                  </button>
                </div>
                <button
                  onClick={() => { add(product, qty, variant); setIsOpen(true); }}
                  disabled={!product.in_stock}
                  data-testid="product-add-to-bag"
                  className="flex-1 min-w-[200px] flex items-center justify-center gap-3 bg-gold text-white text-xs uppercase tracking-[0.3em] px-8 py-4 hover:bg-champagne transition-colors duration-300 disabled:opacity-40"
                >
                  <ShoppingBag size={15} /> Add to Bag
                </button>
                <button
                  onClick={() => { toggle(product.id); toast.success(has(product.id) ? "Removed from wishlist" : "Saved to wishlist"); }}
                  data-testid="product-wishlist-btn"
                  className={`p-4 border transition-colors duration-300 ${has(product.id) ? "border-gold text-gold" : "border-zinc-200 text-zinc-500 hover:border-gold hover:text-gold"}`}
                  aria-label="Toggle wishlist"
                >
                  <Heart size={16} className={has(product.id) ? "fill-gold" : ""} />
                </button>
                <button onClick={share} data-testid="product-share-btn" className="p-4 border border-zinc-200 text-zinc-500 hover:border-gold hover:text-gold transition-colors duration-300" aria-label="Share">
                  <Share2 size={16} />
                </button>
              </div>
              {brand?.website && (
                <a href={brand.website} target="_blank" rel="noopener noreferrer" className="mt-6 text-[10px] uppercase tracking-[0.3em] text-zinc-400 hover:text-gold transition-colors duration-300" data-testid="product-brand-link">
                  Also available at {brand.name} ↗
                </a>
              )}
              <DeliveryCheck product={product} qty={qty} />
              {reelId(reelUrl) && (
                <div className="mt-8 border border-zinc-200 p-5" data-testid="product-reel">
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 flex items-center gap-2">
                      <Instagram size={13} className="text-gold" /> Watch {product.name} in action
                    </p>
                    {brand?.instagram_profile && (
                      <a href={brand.instagram_profile} target="_blank" rel="noopener noreferrer" data-testid="reel-follow-brand" className="text-[9px] uppercase tracking-[0.2em] text-gold hover:text-champagne transition-colors duration-300 shrink-0">
                        Follow {brand.name}
                      </a>
                    )}
                  </div>
                  <div className="relative rounded-lg overflow-hidden border border-zinc-100 bg-zinc-50 aspect-[9/14] max-w-[280px]">
                    <iframe
                      src={embedUrl(reelUrl)}
                      className="w-full h-full"
                      frameBorder="0"
                      scrolling="no"
                      allow="encrypted-media"
                      loading="lazy"
                      title={`${product.name} reel`}
                    />
                    <div className="absolute bottom-3 left-3 right-3 bg-white/90 backdrop-blur-md px-4 py-3 rounded-lg flex items-center gap-3" data-testid="reel-product-tag">
                      <div className="flex-1 min-w-0">
                        <p className="text-[9px] uppercase tracking-[0.2em] text-gold">Featured in this reel</p>
                        <p className="text-xs text-zinc-900 leading-snug line-clamp-1 mt-0.5">{product.name}</p>
                      </div>
                      <button
                        onClick={() => {
                          add(product, 1, variant);
                          setIsOpen(true);
                          api.post("/analytics/reel-add", { reel_id: reelId(reelUrl), product_id: product.id, surface: "pdp" }).catch(() => {});
                        }}
                        data-testid="reel-add-btn"
                        className="shrink-0 bg-gold text-white text-[9px] uppercase tracking-[0.2em] px-3.5 py-2 hover:bg-champagne transition-colors duration-300"
                      >
                        {inr(displayPrice)} · Add
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <FrequentlyBoughtTogether product={product} />

      <ProductSlider
        eyebrow="More to Love"
        title={<>Suggested <em className="text-gold">Products</em></>}
        products={suggested}
        brandName={(slug) => brands.find((b) => b.slug === slug)?.name || slug}
        testId="suggested-slider"
        viewAll={
          <button onClick={() => navigate(`/shop?brand=${product.brand_slug}`)} className="group hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-zinc-500 hover:text-gold transition-colors duration-300" data-testid="suggested-view-all">
            View all <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        }
      />

      <ProductSlider
        eyebrow="You May Also Like"
        title={<>Customers <em className="text-gold">Also Viewed</em></>}
        products={alsoViewed}
        brandName={(slug) => brands.find((b) => b.slug === slug)?.name || slug}
        testId="also-viewed-slider"
        viewAll={
          product.category ? (
            <button onClick={() => navigate(`/shop?q=${encodeURIComponent(product.category)}`)} className="group hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-zinc-500 hover:text-gold transition-colors duration-300" data-testid="also-viewed-view-all">
              View all <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          ) : null
        }
      />

      <Reviews productId={id} />
      <Footer brands={brands} />
    </main>
  );
}
